import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, User, Clock, MapPin } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Instructor {
  id: string;
  name: string;
  email: string;
  phone?: string;
  is_active: boolean;
}

interface ClassLevel {
  id: string;
  name: string;
  description?: string;
  color: string;
}

interface ClassSchedule {
  id: string;
  day_of_week: number;
  time_slot: string;
  court_number: number;
  max_students: number;
  instructor_id?: string;
  level_id?: string;
  is_active: boolean;
  instructor?: Instructor;
  level?: ClassLevel;
}

const DAYS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];
const TIME_SLOTS = ['07:00:00', '08:00:00', '09:00:00', '16:00:00', '17:00:00'];

export default function AdminPanel() {
  const [activeTab, setActiveTab] = useState('schedules');
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [levels, setLevels] = useState<ClassLevel[]>([]);
  const [schedules, setSchedules] = useState<ClassSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingSchedule, setEditingSchedule] = useState<ClassSchedule | null>(null);

  // Formularios
  const [instructorForm, setInstructorForm] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const [levelForm, setLevelForm] = useState({
    name: '',
    description: '',
    color: '#3B82F6'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchInstructors(),
        fetchLevels(),
        fetchSchedules()
      ]);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const fetchInstructors = async () => {
    const { data, error } = await supabase
      .from('instructors')
      .select('*')
      .order('name');
    
    if (error) throw error;
    setInstructors(data || []);
  };

  const fetchLevels = async () => {
    const { data, error } = await supabase
      .from('class_levels')
      .select('*')
      .order('name');
    
    if (error) throw error;
    setLevels(data || []);
  };

  const fetchSchedules = async () => {
    const { data, error } = await supabase
      .from('class_schedules')
      .select(`
        *,
        instructor:instructors(id, name, email, is_active),
        level:class_levels(id, name, color)
      `)
      .order('day_of_week')
      .order('time_slot');
    
    if (error) throw error;
    setSchedules(data || []);
  };

  const handleCreateInstructor = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      console.log('🔧 Intentando crear instructor:', instructorForm);
      
      // Preparar los datos del instructor
      const instructorData = {
        name: instructorForm.name.trim(),
        email: instructorForm.email.trim(),
        phone: instructorForm.phone.trim() || null,
        is_active: true
      };

      console.log('📝 Datos del instructor a crear:', instructorData);
      const { error } = await supabase
        .from('instructors')
        .insert([instructorData]);
      
      if (error) throw error;
      
      console.log('✅ Instructor creado exitosamente');
      setInstructorForm({ name: '', email: '', phone: '' });
      fetchInstructors();
      alert('Instructor creado exitosamente');
    } catch (error: any) {
      console.error('Error completo:', error);
      if (error.message) {
        alert('Error al crear instructor: ' + error.message);
      } else {
        alert('Error de conexión. Verifica tu conexión a internet y que estés autenticado.');
      }
    }
  };

  const handleCreateLevel = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const { error } = await supabase
        .from('class_levels')
        .insert([levelForm]);
      
      if (error) throw error;
      
      setLevelForm({ name: '', description: '', color: '#3B82F6' });
      fetchLevels();
      alert('Nivel creado exitosamente');
    } catch (error: any) {
      alert('Error al crear nivel: ' + error.message);
    }
  };

  const handleUpdateSchedule = async (schedule: ClassSchedule) => {
    try {
      const { error } = await supabase
        .from('class_schedules')
        .update({
          instructor_id: schedule.instructor_id,
          level_id: schedule.level_id,
          max_students: schedule.max_students,
          is_active: schedule.is_active
        })
        .eq('id', schedule.id);
      
      if (error) throw error;
      
      setEditingSchedule(null);
      fetchSchedules();
      alert('Horario actualizado exitosamente');
    } catch (error: any) {
      alert('Error al actualizar horario: ' + error.message);
    }
  };

  const toggleInstructorStatus = async (instructor: Instructor) => {
    try {
      const { error } = await supabase
        .from('instructors')
        .update({ is_active: !instructor.is_active })
        .eq('id', instructor.id);
      
      if (error) throw error;
      fetchInstructors();
    } catch (error: any) {
      alert('Error al actualizar instructor: ' + error.message);
    }
  };

  const formatTime = (time: string) => {
    return time.slice(0, 5);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Panel de Administración</h2>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-blue-800 text-sm">
            <strong>Acceso Público:</strong> Cualquier persona con este enlace puede gestionar instructores, niveles y horarios de clases.
          </p>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex space-x-4 mb-6">
        <button
          onClick={() => setActiveTab('schedules')}
          className={`px-4 py-2 rounded-lg ${
            activeTab === 'schedules'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Horarios
        </button>
        <button
          onClick={() => setActiveTab('instructors')}
          className={`px-4 py-2 rounded-lg ${
            activeTab === 'instructors'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Instructores
        </button>
        <button
          onClick={() => setActiveTab('levels')}
          className={`px-4 py-2 rounded-lg ${
            activeTab === 'levels'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Niveles
        </button>
      </div>

      {/* Gestión de Horarios */}
      {activeTab === 'schedules' && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Gestión de Horarios</h3>
          <div className="space-y-4">
            {DAYS.map((day, dayIndex) => (
              <div key={day} className="border rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">{day}</h4>
                <div className="space-y-2">
                  {TIME_SLOTS.map((timeSlot) => {
                    const daySchedules = schedules.filter(
                      s => s.day_of_week === dayIndex + 1 && s.time_slot === timeSlot
                    );

                    return (
                      <div key={timeSlot} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="text-sm font-medium text-gray-600 flex items-center">
                          <Clock className="h-4 w-4 mr-2" />
                          {formatTime(timeSlot)}
                        </div>
                        <div className="space-y-2">
                          {daySchedules.length === 0 && (
                            <div className="text-sm text-red-600 italic">
                              ⚠️ Faltan horarios para este slot
                            </div>
                          )}
                          {daySchedules.map((schedule) => (
                          <div key={schedule.id} className="border border-gray-200 rounded p-3">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <MapPin className="h-4 w-4 text-gray-500" />
                                <span className="text-sm font-medium">Cancha {schedule.court_number}</span>
                              </div>
                              {daySchedules.length < 2 && (
                                <span className="text-xs text-orange-600 font-medium">
                                  ⚠️ Falta cancha {daySchedules[0]?.court_number === 1 ? '2' : '1'}
                                </span>
                              )}
                            </div>
                            {editingSchedule?.id === schedule.id ? (
                              <div className="space-y-2">
                                <select
                                  value={editingSchedule.instructor_id || ''}
                                  onChange={(e) => setEditingSchedule({
                                    ...editingSchedule,
                                    instructor_id: e.target.value || undefined
                                  })}
                                  className="w-full text-sm border border-gray-300 rounded px-2 py-1"
                                >
                                  <option value="">Sin instructor</option>
                                  {instructors.filter(i => i.is_active).map(instructor => (
                                    <option key={instructor.id} value={instructor.id}>
                                      {instructor.name}
                                    </option>
                                  ))}
                                </select>

                                <select
                                  value={editingSchedule.level_id || ''}
                                  onChange={(e) => setEditingSchedule({
                                    ...editingSchedule,
                                    level_id: e.target.value || undefined
                                  })}
                                  className="w-full text-sm border border-gray-300 rounded px-2 py-1"
                                >
                                  <option value="">Sin nivel</option>
                                  {levels.map(level => (
                                    <option key={level.id} value={level.id}>
                                      {level.name}
                                    </option>
                                  ))}
                                </select>

                                <input
                                  type="number"
                                  min="1"
                                  max="8"
                                  value={editingSchedule.max_students}
                                  onChange={(e) => setEditingSchedule({
                                    ...editingSchedule,
                                    max_students: parseInt(e.target.value)
                                  })}
                                  className="w-full text-sm border border-gray-300 rounded px-2 py-1"
                                />

                                <div className="flex items-center space-x-2">
                                  <input
                                    type="checkbox"
                                    checked={editingSchedule.is_active}
                                    onChange={(e) => setEditingSchedule({
                                      ...editingSchedule,
                                      is_active: e.target.checked
                                    })}
                                  />
                                  <span className="text-sm">Activo</span>
                                </div>

                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleUpdateSchedule(editingSchedule)}
                                    className="flex items-center space-x-1 px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                                  >
                                    <Save className="h-3 w-3" />
                                    <span>Guardar</span>
                                  </button>
                                  <button
                                    onClick={() => setEditingSchedule(null)}
                                    className="flex items-center space-x-1 px-2 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700"
                                  >
                                    <X className="h-3 w-3" />
                                    <span>Cancelar</span>
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div>
                                <div className="flex items-center justify-between mb-1">
                                  <button
                                    onClick={() => setEditingSchedule(schedule)}
                                    className="p-1 text-gray-500 hover:text-blue-600"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </button>
                                </div>
                                
                                <div className="space-y-1 text-xs text-gray-600">
                                  <div>Instructor: {schedule.instructor?.name || 'Sin asignar'}</div>
                                  <div>Nivel: {schedule.level?.name || 'Sin asignar'}</div>
                                  <div>Cupos: {schedule.max_students}</div>
                                  <div>Estado: {schedule.is_active ? 'Activo' : 'Inactivo'}</div>
                                </div>
                              </div>
                            )}
                          </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Gestión de Instructores */}
      {activeTab === 'instructors' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Gestión de Instructores</h3>
          </div>

          {/* Formulario para crear instructor */}
          <form onSubmit={handleCreateInstructor} className="mb-6 p-4 border rounded-lg">
            <h4 className="font-medium mb-3">Agregar Nuevo Instructor</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="Nombre"
                required
                value={instructorForm.name}
                onChange={(e) => setInstructorForm({...instructorForm, name: e.target.value})}
                className="border border-gray-300 rounded px-3 py-2"
              />
              <input
                type="email"
                placeholder="Email"
                required
                value={instructorForm.email}
                onChange={(e) => setInstructorForm({...instructorForm, email: e.target.value})}
                className="border border-gray-300 rounded px-3 py-2"
              />
              <input
                type="tel"
                placeholder="Teléfono"
                value={instructorForm.phone}
                onChange={(e) => setInstructorForm({...instructorForm, phone: e.target.value})}
                className="border border-gray-300 rounded px-3 py-2"
              />
            </div>
            <button
              type="submit"
              className="mt-3 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Agregar Instructor
            </button>
          </form>

          {/* Lista de instructores */}
          <div className="space-y-2">
            {instructors.map((instructor) => (
              <div key={instructor.id} className="flex items-center justify-between p-3 border rounded">
                <div className="flex items-center space-x-3">
                  <User className="h-5 w-5 text-gray-500" />
                  <div>
                    <div className="font-medium">{instructor.name}</div>
                    <div className="text-sm text-gray-600">{instructor.email}</div>
                    {instructor.phone && (
                      <div className="text-sm text-gray-600">{instructor.phone}</div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => toggleInstructorStatus(instructor)}
                  className={`px-3 py-1 rounded text-sm ${
                    instructor.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {instructor.is_active ? 'Activo' : 'Inactivo'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Gestión de Niveles */}
      {activeTab === 'levels' && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Gestión de Niveles</h3>

          {/* Formulario para crear nivel */}
          <form onSubmit={handleCreateLevel} className="mb-6 p-4 border rounded-lg">
            <h4 className="font-medium mb-3">Agregar Nuevo Nivel</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="Nombre del nivel"
                required
                value={levelForm.name}
                onChange={(e) => setLevelForm({...levelForm, name: e.target.value})}
                className="border border-gray-300 rounded px-3 py-2"
              />
              <input
                type="text"
                placeholder="Descripción"
                value={levelForm.description}
                onChange={(e) => setLevelForm({...levelForm, description: e.target.value})}
                className="border border-gray-300 rounded px-3 py-2"
              />
              <input
                type="color"
                value={levelForm.color}
                onChange={(e) => setLevelForm({...levelForm, color: e.target.value})}
                className="border border-gray-300 rounded px-3 py-2 h-10"
              />
            </div>
            <button
              type="submit"
              className="mt-3 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Agregar Nivel
            </button>
          </form>

          {/* Lista de niveles */}
          <div className="space-y-2">
            {levels.map((level) => (
              <div key={level.id} className="flex items-center justify-between p-3 border rounded">
                <div className="flex items-center space-x-3">
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: level.color }}
                  ></div>
                  <div>
                    <div className="font-medium">{level.name}</div>
                    {level.description && (
                      <div className="text-sm text-gray-600">{level.description}</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}